/*
  Artelle AI Generator — shared storefront helper
  ------------------------------------------------
  - Keeps AI image bytes in IndexedDB (not localStorage/sessionStorage).
  - Keeps only IDs/metadata for the current browser-tab visit in sessionStorage.
  - Exposes window.ArtelleAI for Header + Paint-by-Numbers product picker.
  - NEVER place an OpenAI/API secret in this file.
*/
(function () {
  if (window.ArtelleAI) return;

  const DB_NAME = 'artelle-ai-generator-v1';
  const DB_VERSION = 1;
  const STORE = 'generations';
  const SESSION_KEY = 'artelle_ai_session_id';
  const CURRENT_KEY = 'artelle_ai_current_generation_id';
  const HISTORY_KEY = 'artelle_ai_generation_history';
  const MAX_HISTORY = 8;

  const randomId = (prefix) => {
    if (window.crypto && crypto.randomUUID) return `${prefix}_${crypto.randomUUID()}`;
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  };

  const getSessionId = () => {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = randomId('session');
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  };

  const openDb = () => new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt', { unique: false });
        store.createIndex('sessionId', 'sessionId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB could not be opened.'));
  });

  const txRequest = async (mode, callback) => {
    const db = await openDb();
    try {
      return await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        let result;
        try {
          result = callback(store);
        } catch (error) {
          reject(error);
          return;
        }

        if (result && typeof result.onsuccess !== 'undefined') {
          result.onsuccess = () => resolve(result.result);
          result.onerror = () => reject(result.error);
        } else {
          tx.oncomplete = () => resolve(result);
          tx.onerror = () => reject(tx.error);
          tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction was aborted.'));
        }
      });
    } finally {
      db.close();
    }
  };

  const putGeneration = (record) => txRequest('readwrite', (store) => store.put(record));
  const getGeneration = (id) => id ? txRequest('readonly', (store) => store.get(id)) : Promise.resolve(null);
  const deleteGeneration = (id) => id ? txRequest('readwrite', (store) => store.delete(id)) : Promise.resolve();
  const getAllGenerations = () => txRequest('readonly', (store) => store.getAll());

  const getHistoryIds = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(HISTORY_KEY) || '[]');
      return Array.isArray(value) ? value.filter(Boolean) : [];
    } catch (_) {
      return [];
    }
  };

  const setHistoryIds = (ids) => {
    const clean = Array.from(new Set(ids.filter(Boolean))).slice(0, MAX_HISTORY);
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(clean));
    return clean;
  };

  const rememberGeneration = (id) => {
    if (!id) return [];
    const ids = getHistoryIds().filter((existing) => existing !== id);
    ids.unshift(id);
    const clean = setHistoryIds(ids);
    sessionStorage.setItem(CURRENT_KEY, id);
    return clean;
  };

  const setCurrentGenerationId = (id) => {
    if (id) sessionStorage.setItem(CURRENT_KEY, id);
    else sessionStorage.removeItem(CURRENT_KEY);
  };

  const getCurrentGenerationId = () => sessionStorage.getItem(CURRENT_KEY) || '';

  const base64ToBlob = (base64, mimeType = 'image/png') => {
    const clean = String(base64 || '').replace(/^data:[^;]+;base64,/, '');
    const binary = atob(clean);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mimeType });
  };

  const responseToBlob = async (response, json) => {
    if (json?.image_base64) {
      return base64ToBlob(json.image_base64, json.mime_type || 'image/png');
    }

    if (json?.b64_json) {
      return base64ToBlob(json.b64_json, json.mime_type || 'image/png');
    }

    if (json?.image_url) {
      const imageResponse = await fetch(json.image_url, { mode: 'cors', credentials: 'omit' });
      if (!imageResponse.ok) throw new Error('Generated image URL could not be downloaded.');
      return imageResponse.blob();
    }

    const responseType = response.headers.get('content-type') || '';
    if (responseType.startsWith('image/')) return response.blob();

    throw new Error(json?.message || json?.error || 'The generator did not return an image.');
  };

  const generate = async ({ prompt, endpoint, extra = {} }) => {
    prompt = String(prompt || '').trim();
    endpoint = String(endpoint || '').trim();

    if (prompt.length < 2) throw new Error('Please describe the artwork you want.');
    if (prompt.length > 500) throw new Error('Please keep your description under 500 characters.');
    if (!endpoint) throw new Error('AI generator endpoint is not configured.');

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json, image/*'
      },
      body: JSON.stringify({ prompt, ...extra })
    });

    let json = null;
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      try { json = await response.json(); } catch (_) { json = null; }
    }

    if (!response.ok) {
      const message = json?.message || json?.error?.message || json?.error || `Generation failed (${response.status}).`;
      throw new Error(message);
    }

    const blob = await responseToBlob(response, json);
    if (!blob || !blob.size) throw new Error('Generated image was empty.');

    const id = json?.generation_id || randomId('gen');
    const record = {
      id,
      prompt,
      blob,
      mimeType: blob.type || json?.mime_type || 'image/png',
      createdAt: Date.now(),
      sessionId: getSessionId()
    };

    await putGeneration(record);
    rememberGeneration(record.id);
    return record;
  };

  const listSessionGenerations = async () => {
    const ids = getHistoryIds();
    if (!ids.length) return [];
    const records = await Promise.all(ids.map((id) => getGeneration(id)));
    return records.filter((record) => record && record.sessionId === getSessionId());
  };

  const generationToFile = (record) => {
    if (!record?.blob) return null;
    const mimeType = record.mimeType || record.blob.type || 'image/png';
    const ext = mimeType.includes('webp') ? 'webp' : mimeType.includes('jpeg') || mimeType.includes('jpg') ? 'jpg' : 'png';
    return new File([record.blob], `ai-${record.id}.${ext}`, {
      type: mimeType,
      lastModified: record.createdAt || Date.now()
    });
  };

  const createObjectUrl = (record) => record?.blob ? URL.createObjectURL(record.blob) : '';

  const cleanupOldRecords = async () => {
    try {
      const cutoff = Date.now() - (24 * 60 * 60 * 1000);
      const all = await getAllGenerations();
      await Promise.all(all.filter((item) => (item.createdAt || 0) < cutoff).map((item) => deleteGeneration(item.id)));
    } catch (_) {}
  };

  // Fire-and-forget cleanup. Current visit is identified by sessionStorage.
  getSessionId();
  cleanupOldRecords();

  window.ArtelleAI = {
    generate,
    getGeneration,
    listSessionGenerations,
    generationToFile,
    createObjectUrl,
    rememberGeneration,
    getCurrentGenerationId,
    setCurrentGenerationId,
    getSessionId
  };
})();
